import { useState } from "react";
import PlatformBrand from "../components/PlatformBrand";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Sun, Moon, Buildings, Rocket, Lightning, Crown, ShieldCheck, CheckCircle } from "@phosphor-icons/react";
import API from "../lib/api";

const PLANS = [
  { id: "starter", name: "Starter", price: "29", icon: Rocket, color: "border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-950/20", accent: "text-blue-600", desc: "5 users, 10 projects" },
  { id: "professional", name: "Professional", price: "79", icon: Lightning, color: "border-purple-300 dark:border-purple-700 bg-purple-50/50 dark:bg-purple-950/20", accent: "text-purple-600", desc: "20 users, 50 projects" },
  { id: "enterprise", name: "Enterprise", price: "199", icon: Crown, color: "border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20", accent: "text-amber-600", desc: "Unlimited everything" },
  { id: "one_time_licence", name: "Licence", price: "999", icon: ShieldCheck, color: "border-emerald-300 dark:border-emerald-700 bg-emerald-50/50 dark:bg-emerald-950/20", accent: "text-emerald-600", desc: "One-time, full white-label" },
];

export default function SignupPage() {
  const { formatApiError } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [companyName, setCompanyName] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [plan, setPlan] = useState("starter");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (step === 1) {
      if (!companyName.trim() || !name.trim()) {
        setError("Company name and your name are required");
        return;
      }
      setStep(2);
      return;
    }
    setLoading(true);
    try {
      const { data } = await API.post("/auth/tenant-signup", {
        company_name: companyName,
        name,
        email,
        password,
        plan,
      });
      // Redirect to setup wizard
      navigate("/setup");
      window.location.reload();
    } catch (err) {
      setError(
        err.response?.data?.detail
          ? formatApiError(err.response.data.detail)
          : "Registration failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const isDark = theme === "dark";

  return (
    <div className="min-h-screen flex" data-testid="signup-page">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center login-panel-dark">
        <div className="relative z-10 px-16 text-white">
          <PlatformBrand variant="login-hero" className="mb-8" />
          <h1 className="text-4xl sm:text-5xl font-bold font-[Chivo] tracking-tight mb-4 leading-tight">
            Start Managing<br />Your Properties
          </h1>
          <p className="text-base leading-relaxed max-w-md text-slate-300">
            Launch your own branded facilities management platform in minutes. No setup fees, 7-day free trial on all plans.
          </p>
          <div className="mt-12 space-y-3">
            {["White-label branding", "Unlimited customisation", "7-day free trial", "No credit card required"].map((item, i) => (
              <div key={item} className="flex items-center gap-2.5 text-slate-400">
                <CheckCircle size={16} weight="fill" className="text-emerald-400" />
                <span className="text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="login-hex-grid" />
        <div className="login-glow login-glow-orange" />
        <div className="login-glow login-glow-blue" />
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md animate-fade-in">
          <div className="flex items-center justify-between mb-8">
            <PlatformBrand variant="login-right" />
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="signup-theme-toggle">
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
          </div>

          {/* Progress indicator */}
          <div className="flex items-center gap-2 mb-6">
            <div className={`h-1.5 flex-1 rounded-full transition-colors ${step >= 1 ? "bg-primary" : "bg-muted"}`} />
            <div className={`h-1.5 flex-1 rounded-full transition-colors ${step >= 2 ? "bg-primary" : "bg-muted"}`} />
          </div>

          <h2 className="text-2xl font-bold font-[Chivo] tracking-tight mb-1">
            {step === 1 ? "Create Your Workspace" : "Account Details"}
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            {step === 1 ? "Tell us about your company" : "Set up your admin account"}
          </p>

          {error && (
            <div className="mb-4 p-3 rounded-md bg-destructive/10 text-destructive text-sm" data-testid="signup-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {step === 1 ? (
              <>
                <div>
                  <Label htmlFor="companyName">Company Name</Label>
                  <div className="relative mt-1">
                    <Buildings size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="companyName"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="Acme Property Services"
                      className="pl-9"
                      required
                      data-testid="signup-company-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="adminName">Your Name</Label>
                  <Input
                    id="adminName"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Smith"
                    className="mt-1"
                    required
                    data-testid="signup-name-input"
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Select a Plan</Label>
                  <div className="grid grid-cols-2 gap-2" data-testid="signup-plan-grid">
                    {PLANS.map((p) => {
                      const Icon = p.icon;
                      const selected = plan === p.id;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setPlan(p.id)}
                          className={`border-2 rounded-lg p-3 text-left transition-all ${selected ? `${p.color} ring-2 ring-primary/30` : "border-border bg-card hover:border-muted-foreground/30"}`}
                          data-testid={`signup-plan-${p.id}`}
                        >
                          <Icon size={16} className={p.accent} weight="fill" />
                          <p className="text-xs font-bold mt-1.5">{p.name}</p>
                          <p className="text-[10px] text-muted-foreground">{p.id === "one_time_licence" ? `£${p.price} once` : `£${p.price}/mo`}</p>
                          <p className="text-[9px] text-muted-foreground mt-0.5">{p.desc}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <Button type="submit" className="w-full" data-testid="signup-next-btn">
                  Continue
                </Button>
              </>
            ) : (
              <>
                <div>
                  <Label htmlFor="signupEmail">Email Address</Label>
                  <Input
                    id="signupEmail"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="mt-1"
                    required
                    data-testid="signup-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="signupPassword">Password</Label>
                  <Input
                    id="signupPassword"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min. 8 characters"
                    className="mt-1"
                    required
                    minLength={8}
                    data-testid="signup-password-input"
                  />
                </div>
                <div className="border border-border rounded-lg p-3 bg-muted/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold">{companyName}</p>
                      <p className="text-[10px] text-muted-foreground capitalize">{plan.replace("_", " ")} plan &middot; 7-day trial</p>
                    </div>
                    <button type="button" onClick={() => setStep(1)} className="text-[10px] text-primary hover:underline" data-testid="signup-back-btn">
                      Change
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="signup-submit-btn">
                  {loading ? "Creating workspace..." : "Create Workspace"}
                </Button>
              </>
            )}
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="text-primary font-medium hover:underline" data-testid="signup-login-link">
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
